import { useEffect, useState } from 'react';

import { getTeamsReportRequest, TeamsReportRequest } from '../../../api/reports/teamsReports';
import { POLL_INTERVAL_MS, POLL_TIMEOUT_MS } from '../constants';
import { isReportInProgress } from '../utils';

/**
 * Polls one report every 5 s while it is pending or running. Stops on any other status,
 * on an error, or after 15 minutes (`timedOut`) — the Reports page carries on from there.
 * Bumping `reloadKey` reads it again without blanking what is on screen.
 */
export const useReportStatusPolling = (companyId?: string, requestId?: string | null, reloadKey = 0) => {
  const [report, setReport] = useState<TeamsReportRequest | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [timedOut, setTimedOut] = useState(false);

  // A different report: forget the previous one
  useEffect(() => {
    setReport(null);
  }, [companyId, requestId]);

  useEffect(() => {
    setError(null);
    setTimedOut(false);
    if (!companyId || !requestId) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const startedAt = Date.now();

    const poll = async () => {
      try {
        const { data } = await getTeamsReportRequest(companyId, requestId);
        if (cancelled) return;
        setReport(data);
        if (!isReportInProgress(data.status)) return;
        if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
          setTimedOut(true);
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (!cancelled) setError(e);
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [companyId, requestId, reloadKey]);

  return { report, error, timedOut };
};
